import ComFrame from '../views/layout/ComFrame.vue';
import ComLogin from '../views/ComLogin.vue';
import ComErrorPageNotFound from '../views/ComErrorPageNotFound.vue';
import ComWelcome from '../views/ComWelcome.vue';
import ComGettingStarted from '../views/ComGettingStarted.vue';
import ConfirmTerms from '../views/ConfirmTerms.vue';
import StartupSolShare from '../views/StartupSolShare.vue';
import SerialNumberHow from '../views/SerialNumberHow.vue';
import SerialNumberManually from '../views/SerialNumberManually.vue';
import SerialNumberQRCode from '../views/SerialNumberQRCode.vue';
import SearchingSolshare from '../views/SearchingSolshare.vue';
import SearchingSolshareFound from '../views/SearchingSolshareFound.vue';
import SearchingSolshareOffline from '../views/SearchingSolshareOffline.vue';
import SearchingSolshareNeedPowerCycle from '../views/SearchingSolshareNeedPowerCycle.vue';
import SearchingSolshareAlreadyCom from '../views/SearchingSolshareAlreadyCom.vue';
import SoftwareUpdate from '../views/SoftwareUpdate.vue';
import SoftwareUpdateSucceeded from '../views/SoftwareUpdateSucceeded.vue';
import SoftwareUpdateFailed from '../views/SoftwareUpdateFailed.vue';
import CommissionInProgress from '../views/CommissionInProgress.vue';
import ProjectSelect from '../views/project-site/ProjectSelect.vue';
import ProjectListAll from '../views/project-site/ProjectListAll.vue';
import ProjectCreate from '../views/project-site/ProjectCreate.vue';
import ProjectEdit from '../views/project-site/ProjectEdit.vue';
import ProjectWarrantyContact from '../views/project-site/ProjectWarrantyContact.vue';
import ProjectAddSite from '../views/project-site/ProjectAddSite.vue';
import ProjectSelectSite from '../views/project-site/ProjectSelectSite.vue';
import ConfigureSolshareConfirmSite from '../views/ConfigureSolshareConfirmSite.vue';
import ConfigureSolshareSystemSpec from '../views/ConfigureSolshareSystemSpec.vue';
import ConfigureSolshareReviewLiveConnections from '../views/ConfigureSolshareReviewLiveConnections.vue';
import ConfigureSolshareConnections from '../views/ConfigureSolshareConnections.vue';
import StartCommissioning from '../views/StartCommissioning.vue';
import CommissionSucceeded from '../views/CommissionSucceeded.vue';
import ConfigureSolshareAllocations from '../views/ConfigureSolshareAllocations.vue';
import ConfigureSolshareAllocationsConfirm from '../views/ConfigureSolshareAllocationsConfirm.vue';
import { useDeviceSpecificationStore } from '../stores/deviceSpecificationStore';
import type { RouteLocationNormalized } from 'vue-router';
import type { NavigationGuardNext } from 'vue-router';
import ErrorRouter from '../views/error-pages/ErrorRouter.vue';
import Troubleshoot from '../views/error-pages/Troubleshoot.vue';
import DebugTroubleshootPage from '../views/error-pages/DebugTroubleshootPage.vue';
import { loadCommissioningLocales, setupCommissioningLocaleWatcher } from '../locales/loader';
import InviteAssetManager from '../views/InviteAssetManager.vue';
import CommissionDone from '../views/CommissionDone.vue';
import { castProjectId } from './utils';
import NetPromoterScore from '../views/NetPromoterScore.vue';
import PostCommissionChecks from '../views/PostCommissionChecks.vue';
import ComAccountActivated from '../views/ComAccountActivated.vue';
import ComPasswordReset from '../views/ComPasswordReset.vue';
import SearchingSolshareRecommissionContactSupport from '../views/SearchingSolshareRecommissionContactSupport.vue';
import SearchingSolshareRecommissionSetup from '../views/SearchingSolshareRecommissionSetup.vue';
import SearchingSolshareRecommissionPrepareFailed from '../views/SearchingSolshareRecommissionPrepareFailed.vue';

const commissioningRoutes = [
  {
    path: '/commissioning',
    component: ComFrame,
    meta: {
      requiresComAccess: true,
      requiresComAuth: true,
    },
    beforeEnter: (
      _to: RouteLocationNormalized,
      _from: RouteLocationNormalized,
      next: NavigationGuardNext,
    ) => {
      loadCommissioningLocales();
      setupCommissioningLocaleWatcher();
      next();
    },
    children: [
      {
        path: '',
        redirect: { name: 'ComWelcome' },
      },
      {
        path: 'qrcode/:serialNumber',
        redirect: (_to: RouteLocationNormalized) => {
          return { name: 'ComLogin' };
        },
      },
      {
        path: 'login',
        name: 'ComLogin',
        component: ComLogin,
      },
      {
        path: 'account-activated',
        name: 'ComAccountActivated',
        component: ComAccountActivated,
      },
      {
        path: 'password-reset/:token',
        name: 'ComPasswordReset',
        component: ComPasswordReset,
      },
      {
        path: 'welcome',
        name: 'ComWelcome',
        component: ComWelcome,
      },
      {
        path: 'getting-started',
        name: 'ComGettingStarted',
        component: ComGettingStarted,
        meta: {
          progressStep: 1,
        },
      },
      {
        path: 'confirm-terms',
        name: 'ConfirmTerms',
        component: ConfirmTerms,
        meta: {
          progressStep: 1,
        },
      },
      {
        path: 'startup-solshare',
        name: 'StartupSolShare',
        component: StartupSolShare,
        meta: {
          progressStep: 1,
        },
      },
      {
        path: 'enter-serial-number',
        name: 'SerialNumberHow',
        component: SerialNumberHow,
        meta: {
          progressStep: 1,
        },
      },
      {
        path: 'enter-serial-number/manually',
        name: 'SerialNumberManually',
        component: SerialNumberManually,
        meta: {
          progressStep: 1,
        },
      },
      {
        path: 'enter-serial-number/qr-code',
        name: 'SerialNumberQRCode',
        component: SerialNumberQRCode,
        meta: {
          progressStep: 1,
        },
      },
      {
        path: 'searching-solshare/:serialNumber',
        meta: {
          progressStep: 1,
        },
        children: [
          {
            path: '',
            name: 'SearchingSolshare',
            component: SearchingSolshare,
          },
          {
            path: 'found',
            name: 'SearchingSolshareFound',
            component: SearchingSolshareFound,
          },
          {
            path: 'offline',
            name: 'SearchingSolshareOffline',
            component: SearchingSolshareOffline,
          },
          {
            path: 'need-power-cycle',
            name: 'SearchingSolshareNeedPowerCycle',
            component: SearchingSolshareNeedPowerCycle,
          },
        ],
      },
      {
        path: 'recommission-solshare',
        meta: {
          requiresSerialNumber: true,
        },
        children: [
          {
            path: 'already-commissioned',
            name: 'SearchingSolshareAlreadyCom',
            component: SearchingSolshareAlreadyCom,
          },
          {
            path: 'recommission-contact-support',
            name: 'SearchingSolshareRecommissionContactSupport',
            component: SearchingSolshareRecommissionContactSupport,
          },
          {
            path: 'recommission-setup',
            name: 'SearchingSolshareRecommissionSetup',
            component: SearchingSolshareRecommissionSetup,
          },
          {
            path: 'recommission-prepare-failed',
            name: 'SearchingSolshareRecommissionPrepareFailed',
            component: SearchingSolshareRecommissionPrepareFailed,
          },
        ],
      },
      {
        path: 'software-update',
        meta: {
          requiresSerialNumber: true,
        },
        children: [
          {
            path: '',
            name: 'SoftwareUpdate',
            component: SoftwareUpdate,
          },
          {
            path: 'succeeded',
            name: 'SoftwareUpdateSucceeded',
            component: SoftwareUpdateSucceeded,
          },
          {
            path: 'failed',
            name: 'SoftwareUpdateFailed',
            component: SoftwareUpdateFailed,
          },
        ],
      },
      {
        path: 'projects',
        meta: {
          requiresSerialNumber: true,
          progressStep: 2,
        },
        children: [
          {
            path: '',
            name: 'ProjectSelect',
            component: ProjectSelect,
          },
          {
            path: 'all',
            name: 'ProjectListAll',
            component: ProjectListAll,
          },
          {
            path: 'new',
            name: 'ProjectCreate',
            component: ProjectCreate,
          },
          {
            path: ':projectId(\\d+)/edit',
            name: 'ProjectEdit',
            component: ProjectEdit,
            props: castProjectId,
          },
          {
            path: ':projectId(\\d+)/warranty-contact',
            name: 'ProjectWarrantyContact',
            component: ProjectWarrantyContact,
            props: castProjectId,
          },
          {
            path: ':projectId(\\d+)/add-site',
            name: 'ProjectAddSite',
            component: ProjectAddSite,
            props: castProjectId,
          },
          {
            path: ':projectId(\\d+)/select-site',
            name: 'ProjectSelectSite',
            component: ProjectSelectSite,
            props: castProjectId,
          },
        ],
      },
      {
        path: 'configure-solshare',
        meta: {
          requiresSerialNumber: true,
          requiresConfirmedAddress: true,
          progressStep: 3,
        },
        children: [
          {
            path: '',
            name: 'ConfigureSolshareConfirmSite',
            component: ConfigureSolshareConfirmSite,
            meta: {
              requiresAddressEntry: true,
              requiresConfirmedAddress: false,
            },
          },
          {
            path: 'system-spec',
            name: 'ConfigureSolshareSystemSpec',
            component: ConfigureSolshareSystemSpec,
          },
          {
            path: 'review-live-connections',
            name: 'ConfigureSolshareReviewLiveConnections',
            component: ConfigureSolshareReviewLiveConnections,
          },

          {
            path: 'output-connections',
            name: 'ConfigureSolshareConnections',
            component: ConfigureSolshareConnections,
          },
          {
            path: 'allocations',
            name: 'ConfigureSolshareAllocations',
            component: ConfigureSolshareAllocations,
          },
          {
            path: 'allocations-confirm',
            name: 'ConfigureSolshareAllocationsConfirm',
            component: ConfigureSolshareAllocationsConfirm,
            beforeEnter: (
              _to: RouteLocationNormalized,
              _from: RouteLocationNormalized,
              next: NavigationGuardNext,
            ) => {
              const deviceSpecificationStore = useDeviceSpecificationStore();
              if (!deviceSpecificationStore.draft.allocations) {
                next({ name: 'ConfigureSolshareAllocations' });
              } else {
                next();
              }
            },
          },
        ],
      },
      {
        path: 'commission-solshare',
        meta: {
          requiresSerialNumber: true,
          progressStep: 4,
        },
        children: [
          {
            path: '',
            name: 'StartCommissioning',
            component: StartCommissioning,
          },
          {
            path: 'in-progress',
            name: 'CommissionInProgress',
            component: CommissionInProgress,
          },
        ],
      },
      {
        path: 'commission-result',
        meta: {
          requiresSerialNumber: true,
          progressStep: 4,
        },
        children: [
          {
            path: 'failed',
            name: 'CommissionFailed',
            component: ErrorRouter,
          },
          {
            path: 'succeeded',
            name: 'CommissionSucceeded',
            component: CommissionSucceeded,
          },
          {
            path: 'troubleshoot/:errorType',
            name: 'Troubleshoot',
            component: Troubleshoot,
          },
        ],
      },
      {
        path: 'hand-over',
        meta: {
          requiresSerialNumber: true,
          progressStep: 5,
        },
        children: [
          {
            path: 'invite-asset-manager',
            name: 'InviteAssetManager',
            component: InviteAssetManager,
          },
          {
            path: 'nps',
            name: 'NPS',
            component: NetPromoterScore,
          },
          {
            path: 'post-checks',
            name: 'PostCommissionChecks',
            component: PostCommissionChecks,
          },
          {
            path: 'done',
            name: 'CommissionDone',
            component: CommissionDone,
          },
        ],
      },
      {
        path: 'troubleshoot/:serialNumber/:errorType/:country/:failIds?', // For debugging purposes only
        name: 'DebugTroubleshootPage',
        component: DebugTroubleshootPage,
      },
      {
        path: ':pathMatch(.*)*', // Matches all undefined routes
        name: 'ComErrorPageNotFound',
        component: ComErrorPageNotFound,
      },
    ],
  },
];

export default commissioningRoutes;
